import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Download } from "lucide-react";

const FORMATS = [
  { id: "csv", label: "CSV generico", desc: "Foglio Excel/Numbers — colonne standard", ext: "csv" },
  { id: "zucchetti", label: "Zucchetti Paghe Web", desc: "Tracciato voci 100/110/200/210/220", ext: "csv" },
  { id: "teamsystem", label: "TeamSystem (Gecom)", desc: "Causali ORD · STR · FER · PER · MAL", ext: "csv" },
  { id: "inaz", label: "Inaz Paghe", desc: "Eventi giornalieri L/S/F/P/M", ext: "csv" },
  { id: "pdf", label: "Riepilogo PDF", desc: "Report mensile aggregato per dipendente", ext: "pdf" },
];

const MESI = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export default function PayrollPage() {
  const now = new Date();
  const [format, setFormat] = useState("csv");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [preview, setPreview] = useState("");
  const [filename, setFilename] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const yearOptions = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 4 + i);

  const doExport = async () => {
    setLoading(true);
    setPreview("");
    setRows(null);
    try {
      if (format === "pdf") {
        const r = await api.get(`/payroll/export?format=pdf&month=${month}&year=${year}`, { responseType: "blob" });
        const blob = new Blob([r.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const fname = `paghe_${year}${String(month).padStart(2, "0")}_riepilogo.pdf`;
        a.href = url; a.download = fname; a.click();
        URL.revokeObjectURL(url);
        setFilename(fname);
        toast.success("Riepilogo PDF scaricato");
      } else {
        const r = await api.get(`/payroll/export?format=${format}&month=${month}&year=${year}`);
        setPreview(r.data.content || "");
        setFilename(r.data.filename || "");
        setRows(r.data.rows ?? 0);
        const blob = new Blob([r.data.content], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = r.data.filename; a.click();
        URL.revokeObjectURL(url);
        toast.success(`Export ${format.toUpperCase()} scaricato`);
      }
    } catch (e) {
      toast.error("Errore export · " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-12" data-testid="payroll-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Integrazione Software Paghe</div>
        <h1 className="font-display text-4xl">Export paghe</h1>
        <p className="text-[#555] mt-1 text-sm">
          Genera tracciati per Zucchetti, TeamSystem, Inaz, CSV generico o riepilogo PDF.
          Le ore lavorate sono dedotte dalle timbrature valide; ferie, permessi e malattia dalle richieste approvate.
        </p>
      </div>

      {/* Selettore formato */}
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-0 border-t border-l border-black mb-6">
        {FORMATS.map((f, i) => {
          const active = format === f.id;
          const Icon = f.ext === "pdf" ? FileText : FileSpreadsheet;
          return (
            <button
              key={f.id}
              onClick={() => setFormat(f.id)}
              data-testid={`payroll-format-${f.id}`}
              className={`text-left p-6 border-r border-b border-black transition-colors ${
                active ? "bg-[#FF7A29] text-white" : "bg-white hover:bg-[#FFF1E6]"
              }`}
            >
              <Icon className={`w-5 h-5 mb-3 ${active ? "text-white" : "text-[#FF7A29]"}`} />
              <div className="font-display text-lg leading-tight">{f.label}</div>
              <p className={`text-xs mt-2 ${active ? "text-white/85" : "text-[#666]"}`}>{f.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Periodo + azione */}
      <div className="border border-black bg-white p-6 md:p-8">
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#757575] block mb-2">Mese</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              data-testid="payroll-month"
              className="w-full h-11 border border-black bg-white px-3 rounded-none"
            >
              {MESI.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#757575] block mb-2">Anno</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              data-testid="payroll-year"
              className="w-full h-11 border border-black bg-white px-3 rounded-none"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button
              onClick={doExport}
              disabled={loading}
              data-testid="export-payroll-btn"
              className="w-full rounded-none bg-[#FF7A29] hover:bg-black h-11"
            >
              <Download className="w-4 h-4 mr-2" />
              {loading ? "Generazione…" : `Scarica ${format.toUpperCase()}`}
            </Button>
          </div>
        </div>

        {filename && (
          <div className="text-sm text-[#555] mb-3" data-testid="payroll-result-meta">
            File: <span className="font-mono">{filename}</span>
            {rows !== null && <> · <span>{rows} righe</span></>}
          </div>
        )}

        {preview && (
          <div>
            <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Anteprima</div>
            <pre
              data-testid="csv-preview"
              className="bg-black text-green-400 p-4 text-xs overflow-auto max-h-96 whitespace-pre"
            >
              {preview}
            </pre>
          </div>
        )}

        {rows === 0 && !preview && format !== "pdf" && (
          <div className="text-sm text-[#666] mt-2">Nessun dato per il periodo selezionato.</div>
        )}
      </div>
    </div>
  );
}
